import React, { StrictMode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { Auth, API, Storage, Logger, Amplify } from 'aws-amplify';
import * as Sentry from '@sentry/react';
import queryString from 'query-string';
import isMobile from 'is-mobile';
import { createPath } from 'history';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import { H } from 'highlight.run';
import posthog from 'posthog-js';
import App, { UnauthApp } from 'src/legacy/components/App';
import { configureStore } from 'src/store/configureStore';
import history from 'src/history';
import { updateMeta } from 'src/DOMUtils';
import router from 'src/router';
import {
  GetCustomTheme,
  DefaultFlagsContext,
  getClientFlagsContextValue,
} from 'src/context';
import { loadState } from 'src/utils/StorageUtils';
import { UseUnAuthApp } from 'src/utils/AuthUtils';
import { AppShell } from 'src/legacy/components/Layout';
import { atPolyfill } from 'src/polyfills/at';

// Routes that render with zero app chrome (no sidebar, no navbar).
const CHROMELESS_ROUTE_PREFIXES = [
  '/invoices/pay',
  '/payment-links/pay',
  '/sso',
  '/mcp/login',
];
// Pattern-based chromeless routes (for paths with dynamic segments).
const CHROMELESS_ROUTE_PATTERNS = [/^\/store\/[^/]+\/pay/];
const SENTRY_DENY_URLS = [
  /^https?:\/\/www\.googletagmanager\.com(?:\/|$)/i,
  /^https?:\/\/gtm-[a-z0-9-]+\.uc\.r\.appspot\.com(?:\/|$)/i,
];

const initialState = loadState();

// Safari < 15.4 doesnt have support for the Array.prototype.at or
// String.prototype.at method. This polyfill will add support for it.

if (!Array.prototype.at) {
  Object.defineProperty(Array.prototype, 'at', {
    value: function (index) {
      return atPolyfill(this, index);
    },
    configurable: true,
    writable: true,
  });
}

if (!String.prototype.at) {
  Object.defineProperty(String.prototype, 'at', {
    value: function (index) {
      return atPolyfill(this, index);
    },
    configurable: true,
    writable: true,
  });
}

// check if session data is present and if so override userId
// in initialStore and local storage based on session
const { portalConfig, sessionData } = window.App;
const { currentPortalSession, portalIdToSession } = window.App.sessionData;
// we need to get the current portal session related email to set it as LastAuthUser
const { userId } = portalIdToSession[currentPortalSession] || {};
if (userId) {
  window.localStorage.setItem(
    `CognitoIdentityServiceProvider.${portalConfig.AWS.Auth.userPoolWebClientId}.LastAuthUser`,
    userId,
  );
  if (initialState?.user) {
    initialState.user.id = userId;
  }
}

// Global (context) variables that can be easily accessed from any React component
// https://facebook.github.io/react/docs/context.html
const context = {
  isMobile: isMobile(),
  // Initialize a new Redux store
  // http://redux.js.org/docs/basics/UsageWithReact.html
  store: configureStore(
    {
      ...initialState,
      user: {
        ...initialState?.user,
        isClient: portalConfig.viewMode === 'client',
      },
    },
    { history },
    true,
    {
      portalId: portalConfig.portalHeader,
      userId,
    },
  ),
  storeSubscription: null,
  stripeContext: window.App.stripeContext,
  portalConfig,
  flags: window.App.flags,
  posthogBootstrap: window.App.posthogBootstrap,
  sessionData,
  flexibleAuth: window.App.flexibleAuth,
  posthogConfig: window.App.posthogConfig,
};

const customThemeV5 = GetCustomTheme(context.isMobile, context.portalConfig);
const emotionInsertionPoint =
  typeof document !== 'undefined'
    ? document.querySelector('meta[name="emotion-insertion-point"]')
    : null;
const emotionCache = createCache({
  key: 'css',
  ...(emotionInsertionPoint ? { insertionPoint: emotionInsertionPoint } : {}),
});

API.configure(context.portalConfig.AWS.API);
Storage.configure(context.portalConfig.AWS.Storage);
Logger.LOG_LEVEL = window.App.logLevel;

const container = document.getElementById('app');
let currentLocation = history.location;
let appRoot;

const scrollPositionsHistory = {};

function runPostRenderEffects(route, location, isInitialRender) {
  if (isInitialRender) {
    // Switch off the native scroll restoration behavior and handle it manually
    // https://developers.google.com/web/updates/2015/09/history-api-scroll-restoration
    if (window.history && 'scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    const elem = document.getElementById('css');
    if (elem) elem.parentNode.removeChild(elem);

    return;
  }

  updateMeta('description', route.description);
  // Update necessary tags in <head> at runtime here, ie:
  // updateMeta('keywords', route.keywords);
  // updateCustomMeta('og:url', route.canonicalUrl);
  // updateCustomMeta('og:image', route.imageUrl);
  // updateLink('canonical', route.canonicalUrl);
  // etc.

  let scrollX = 0;
  let scrollY = 0;
  const pos = scrollPositionsHistory[location.key];
  if (pos) {
    scrollX = pos.scrollX;
    scrollY = pos.scrollY;
  } else {
    const targetHash = location.hash.substr(1);
    if (targetHash) {
      const target = document.getElementById(targetHash);
      if (target) {
        scrollY = window.pageYOffset + target.getBoundingClientRect().top;
      }
    }
  }

  // Restore the scroll position if it was saved into the state
  // or scroll to the given #hash anchor
  // or scroll to top of the page
  window.scrollTo(scrollX, scrollY);

  // Google Analytics tracking. Don't send 'pageview' event after
  // the initial rendering, as it was already sent
  if (window.ga) {
    window.ga('send', 'pageview', createPath(location));
  }
}

function PostRenderEffects({ route, location, isInitialRender }) {
  React.useEffect(() => {
    runPostRenderEffects(route, location, isInitialRender);
  }, [route, location, isInitialRender]);

  return null;
}

// Re-render the app when window.location changes
async function onLocationChange(location, action) {
  // Remember the latest scroll position for the previous location
  scrollPositionsHistory[currentLocation.key] = {
    scrollX: window.pageXOffset,
    scrollY: window.pageYOffset,
  };
  // Delete stored scroll position for next page if any
  if (action === 'PUSH') {
    delete scrollPositionsHistory[location.key];
  }
  currentLocation = location;
  const isInitialRender = !action;
  try {
    context.pathname = location.pathname;
    context.search = location.search;
    context.hash = location.hash;
    context.query = queryString.parse(location.search);

    // Traverses the list of routes in the order they are defined until
    // it finds the first route that matches provided URL path string
    // and whose action method returns anything other than `undefined`.
    const route = await router.resolve(context);

    context.title = route.title;
    context.description = route.description;
    context.params = route.params || {};

    // Prevent multiple page renders during the routing process
    if (currentLocation.key !== location.key && action !== 'FLAG_UPDATE') {
      return;
    }

    if (route.redirect) {
      history.replace(route.redirect);
      return;
    }

    if (window.gtag) {
      // when gtag is defined we need to call page_view
      window.gtag('event', 'page_view');
    }

    const isUnAuthApp = UseUnAuthApp(context.pathname);
    let AppComponent = App;
    if (isUnAuthApp) {
      // copilot brandded application for login/signup/
      AppComponent = UnauthApp;
    }

    // Wrap authenticated non-chromeless routes with AppShell so the layout
    // frame persists across route transitions. Chromeless pages (payment
    // checkout, SSO redirect) use hasSidebar={false} + hasNavbar={false}.
    const isChromelessRoute =
      CHROMELESS_ROUTE_PREFIXES.some((prefix) =>
        context.pathname.startsWith(prefix),
      ) ||
      CHROMELESS_ROUTE_PATTERNS.some((pattern) =>
        pattern.test(context.pathname),
      );
    const wrapWithAppShell = !isUnAuthApp && !isChromelessRoute;
    const routeContent = wrapWithAppShell
      ? React.createElement(AppShell, null, route.component)
      : route.component;

    const app = React.createElement(
      StrictMode,
      null,
      React.createElement(CacheProvider, {
        value: emotionCache,
        children: React.createElement(StyledEngineProvider, {
          injectFirst: true,
          children: React.createElement(ThemeProvider, {
            theme: customThemeV5,
            children: React.createElement(
              React.Fragment,
              null,
              React.createElement(AppComponent, {
                context,
                children: routeContent,
              }),
              React.createElement(PostRenderEffects, {
                route,
                location,
                isInitialRender,
              }),
            ),
          }),
        }),
      }),
    );
    if (!appRoot) {
      appRoot = isInitialRender
        ? hydrateRoot(container, app)
        : createRoot(container);

      if (!isInitialRender) {
        appRoot.render(app);
      }
    } else {
      appRoot.render(app);
    }
  } catch (error) {
    if (__DEV__) {
      throw error;
    }

    console.error(error);

    // Do a full page reload if error occurs during client-side navigation
    if (!isInitialRender && currentLocation.key === location.key) {
      console.error('RSK will reload your page after error');
      window.location.reload();
    }
  }
}

// Handle client-side navigation by using HTML5 History API
// For more information visit https://github.com/mjackson/history#readme
history.listen(onLocationChange);
onLocationChange(currentLocation);

// In lieu of having portalConfig in our localStorage or Cookie,
// the back button loses the current portalHeader from the config,
// and we won't know which portal you belong to anymore.
if (window.history && window.history.pushState) {
  window.onpopstate = (evt) => {
    if (
      portalConfig.portalHeader === 'workspace' &&
      !window.location.pathname.startsWith('/onboarding')
    ) {
      window.location.reload();
    }
  };
}

// Enable Hot Module Replacement (HMR)
if (module.hot) {
  module.hot.accept('./router', () => {
    onLocationChange(currentLocation, 'HMR_UPDATE');
  });
}

if (window.App.sentryDSN) {
  Sentry.init({
    dsn: window.App.sentryDSN,
    integrations: [Sentry.browserTracingIntegration()],
    denyUrls: SENTRY_DENY_URLS,
    ignoreErrors: [/NS Pushstate prevention/],
    release: window.App.appVersion,
    // Production's server env predates SENTRY_ENVIRONMENT, so it stays the
    // default; staging must set the var or its events would file under prod.
    environment: window.App.sentryEnvironment || 'production',

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 0.2,
  });

  if (window.App.highlightOrgId) {
    H.getSessionURL().then((url) => Sentry.setTag('highlight-session', url));
  }
}

// Listen for PostHog flag updates and sync to context
if (window.App.posthogConfig?.apiKey) {
  // PostHog is initialized via PostHogProvider in App.tsx
  // Listen for flag changes and update context
  const syncPosthogFlags = () => {
    if (posthog.__loaded) {
      context.flags = getClientFlagsContextValue(posthog);
      onLocationChange(currentLocation, 'FLAG_UPDATE');
    }
  };

  // Wait for PostHog to be loaded before syncing
  const checkPosthogLoaded = setInterval(() => {
    if (posthog.__loaded) {
      clearInterval(checkPosthogLoaded);
      syncPosthogFlags();

      // Listen for future flag updates
      posthog.onFeatureFlags(() => {
        syncPosthogFlags();
      });
    }
  }, 100);

  // Clear interval after 10 seconds to avoid infinite checking
  setTimeout(() => clearInterval(checkPosthogLoaded), 10000);
}
